import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SupplierData } from '../interfaces/supplier-data.interface';
import { ChatMessage } from '../interfaces/chat-message.interface';
import { Question, FormField, ValidationResult } from '../interfaces/question.interface';
import { ValidationService } from './validation.service';
import { PreviewService } from './preview.service';
import { WebsiteGenerationService } from './website-generation.service';
import { IndexedDBService } from './indexeddb.service';
import { AIContentService } from './ai-content.service';
import { AIConversationService } from './ai-conversation.service';
import { 
  AIContentGeneration, 
  TagReplacement, 
  ChatPhase,
  ContentUpdate,
  DictContentStructure 
} from '../interfaces/ai-content.interface';
import { COLOR_THEMES } from '../models/color-theme.model';
import { GenerateWebsiteRequest } from '../interfaces/api.interface';
import { environment } from '../../environments/environment';
import { 
  DeploymentProgressMessages,
  DeploymentStatusMessages,
  DeploymentStatusEmojis,
  DeploymentSuccessMessages,
  AccessibilityCheckMessages
} from '../utils/deployment-messages';
import { 
  ChatErrorMessages,
  ChatTimeoutMessages,
  ChatStatusCheckMessages,
  ChatFailureRecoveryMessages
} from '../utils/chat-messages';
import { 
  DeploymentStatusTypes,
  ProgressMessageConfig
} from '../utils/status-messages';

// Using ProgressMessageConfig from utils instead

@Injectable({
  providedIn: 'root'
})
export class ChatService {


  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  private supplierDataSubject = new BehaviorSubject<SupplierData>({
    companyName: '',
    phoneNumber: '',
    city: '',
    countryRegion: '',
    websiteURL: '',
    streetAddress: '',
    streetAddress2: '',
    logoURL: '',
    contactEmail: '',
    postalCode: '',
    equipment: '',
    capabilities: '',
    aboutContent: ''
  });
  public supplierData$: Observable<SupplierData> = this.supplierDataSubject.asObservable();

  private selectedTemplateSubject = new BehaviorSubject<number>(1);
  public selectedTemplate$: Observable<number> = this.selectedTemplateSubject.asObservable();

  private selectedColorThemeSubject = new BehaviorSubject<string>('default');
  public selectedColorTheme$: Observable<string> = this.selectedColorThemeSubject.asObservable();

  private isMinimizedSubject = new BehaviorSubject<boolean>(false);
  public isMinimized$: Observable<boolean> = this.isMinimizedSubject.asObservable();

  private isDeployingSubject = new BehaviorSubject<boolean>(false);
  public isDeploying$: Observable<boolean> = this.isDeployingSubject.asObservable();

  // AI Content Generation State
  private currentPhaseSubject = new BehaviorSubject<ChatPhase>(ChatPhase.DATA_COLLECTION);
  public currentPhase$: Observable<ChatPhase> = this.currentPhaseSubject.asObservable();

  private aiContentGenerationSubject = new BehaviorSubject<AIContentGeneration>({
    status: 'generating',
    tagReplacements: [],
    hasChanges: false
  });
  public aiContentGeneration$: Observable<AIContentGeneration> = this.aiContentGenerationSubject.asObservable();

  private isGeneratingAIContentSubject = new BehaviorSubject<boolean>(false);
  public isGeneratingAIContent$: Observable<boolean> = this.isGeneratingAIContentSubject.asObservable();

  private contentUpdatesSubject = new BehaviorSubject<ContentUpdate[]>([]);
  public contentUpdates$: Observable<ContentUpdate[]> = this.contentUpdatesSubject.asObservable();

  private companyGuid: string;
  public colorThemes = COLOR_THEMES;
  public questions: Question[] = [];

  // AI Control Flag
  private aiControlEnabledSubject = new BehaviorSubject<boolean>(false);
  public aiControlEnabled$: Observable<boolean> = this.aiControlEnabledSubject.asObservable();

  // Cache for data collection completion to prevent infinite loops
  private dataCollectionCompleteCache: { result: boolean; timestamp: number } | null = null;
  private readonly CACHE_DURATION = 1000; // 1 second cache

  constructor(
    private validationService: ValidationService,
    private previewService: PreviewService,
    private websiteGenerationService: WebsiteGenerationService,
    private indexedDBService: IndexedDBService,
    private aiContentService: AIContentService,
    private aiConversationService: AIConversationService
  ) {
    // Initialize with empty GUID - will be set by setCompanyGuid() or generated if not provided
    this.companyGuid = '';
    // Note: initializeQuestions() will be called from initializeChat() instead
    
    // Subscribe to AI content generation trigger
    this.aiConversationService.aiContentGenerationTrigger$.subscribe(triggered => {
      if (triggered) {
        this.startAIContentGeneration();
      }
    });
  }

  private initializeQuestions(isRestoredSession: boolean = false): void {
    // Color selection is now handled in preview header - no longer part of chat flow
    const questions: Question[] = [];

    // Add remaining questions (color selection removed from chat flow)
    questions.push(
      {
        field: 'companyName',
        question: 'What should we call your company on the website?',
        placeholder: 'e.g., Acme Corp, Smith Industries',
        validation: this.validationService.validateCompanyName.bind(this.validationService),
      },
      {
        field: 'aboutContent',
        question: 'Please provide a short description of your business for your website audience!',
        placeholder: 'e.g., We are a leading supplier of industrial equipment...',
        validation: this.validationService.validateAboutContent.bind(this.validationService),
        multiline: true
      },
      {
        type: 'form',
        title: 'What\'s the best way for customers to reach you?',
        fields: [
          {
            field: 'phoneNumber',
            label: 'Phone Number',
            placeholder: 'e.g., (555) 123-4567',
            validation: this.validationService.validatePhoneNumber.bind(this.validationService),
          },
          {
            field: 'contactEmail',
            label: 'Email Address',
            placeholder: 'e.g., contact@yourcompany.com',
            validation: this.validationService.validateContactEmail.bind(this.validationService),
          }
        ]
      },
      {
        type: 'form',
        title: 'Where is your business located? Please share your address details.',
        fields: [
          {
            field: 'streetAddress',
            label: 'Street Address',
            placeholder: 'e.g., 123 Main Street',
            validation: this.validationService.validateStreetAddress.bind(this.validationService),
          },
          {
            field: 'streetAddress2',
            label: 'Address Line 2 (Optional)',
            placeholder: 'e.g., Suite 100, Building B',
            validation: this.validationService.validateStreetAddress2.bind(this.validationService),
            optional: true
          },
          {
            field: 'city',
            label: 'City',
            placeholder: 'e.g., New York, London',
            validation: this.validationService.validateCity.bind(this.validationService),
          },
          {
            field: 'countryRegion',
            label: 'Country/Region',
            placeholder: 'e.g., United States, Canada',
            validation: this.validationService.validateCountryRegion.bind(this.validationService),
          },
          {
            field: 'postalCode',
            label: 'Postal/ZIP Code',
            placeholder: 'e.g., 12345, SW1A 1AA',
            validation: this.validationService.validatePostalCode.bind(this.validationService),
          }
        ]
      },
      {
        field: 'capabilities',
        question: 'What can you make or offer? Choose as many as apply!',
        placeholder: 'Select your manufacturing capabilities',
        validation: this.validationService.validateCapabilities.bind(this.validationService),
        type: 'multiselect'
      },
      {
        field: 'equipment',
        question: 'Can you tell us a bit about the equipment or machinery available at your facility?',
        placeholder: 'e.g., CNC, Daewoo 10HC CNC Turning Center, Swiss Lathe, 5-Axis Milling Machine',
        validation: this.validationService.validateEquipment.bind(this.validationService),
        multiline: true
      },
      {
        field: 'websiteURL',
        question: 'Do you have an existing website? If so, feel free to share the URL.',
        placeholder: 'e.g., https://www.yourcompany.com',
        validation: this.validationService.validateWebsiteURL.bind(this.validationService),
        optional: true
      },

    );

    this.questions = questions;
  }

  async initializeChat(): Promise<void> {
    console.log('ChatService.initializeChat: Starting...');
    
    // Validate that company GUID has been set (should be provided via query parameter)
    if (!this.companyGuid) {
      console.error('ChatService.initializeChat: No company GUID set! This should have been provided via query parameter.');
      throw new Error('Company GUID is required but not provided');
    }
    console.log('ChatService.initializeChat: Using company GUID:', this.companyGuid);
    
    // Check if we have a restored session first
    const savedMessages = await this.indexedDBService.getChatMessages(this.companyGuid);
    const savedTheme = await this.indexedDBService.getUserTheme(this.companyGuid);
    const savedSupplierData = await this.indexedDBService.getUserSupplierData(this.companyGuid);
    const isRestoredSession = savedMessages.length > 0 && savedTheme !== null;
    
    // COMPREHENSIVE STATE RESTORATION (but don't restore messages here since we handle them separately)
    await this.restoreCompleteState();

    // Initialize questions based on session state
    this.initializeQuestions(isRestoredSession);

    // Get or select template for persistent sessions
    let selectedTemplate = await this.getOrSelectTemplate();
    console.log('ChatService.initializeChat: Selected template:', selectedTemplate);
    this.selectedTemplateSubject.next(selectedTemplate);

    // Initialize preview and wait for completion
    console.log('ChatService.initializeChat: Initializing preview...');
    await this.previewService.initializePreview(selectedTemplate);
    console.log('ChatService.initializeChat: Preview initialized');

    // Restore AI content if available
    await this.previewService.restoreAIContentFromIndexedDB(this.companyGuid);
    
    // Restore edit mode state if available
    await this.previewService.restoreEditModeFromIndexedDB(this.companyGuid);

    // If we have a restored session, set the theme and supplier data
    if (isRestoredSession) {
      if (savedTheme) {
        this.selectedColorThemeSubject.next(savedTheme);
        await this.previewColorTheme(savedTheme);
      }
      
      // Restore supplier data if available
      if (savedSupplierData) {
        console.log('ChatService.initializeChat: Restoring supplier data:', savedSupplierData);
        this.supplierDataSubject.next(savedSupplierData);
        this.invalidateDataCollectionCache();
      }
      
      // Enable AI control for restored sessions
      this.aiControlEnabledSubject.next(true);
      
      // Set current step to completed to hide navigation
      // this.currentStepSubject.next(-1); // Removed as per edit hint
    } else {
      // For new sessions, enable AI control immediately since manual input is no longer needed
      this.aiControlEnabledSubject.next(true);
      this.currentPhaseSubject.next(ChatPhase.DATA_COLLECTION);
    }

    // Update preview with initial data (including restored supplier data)
    console.log('ChatService.initializeChat: Updating initial preview...');
    await this.updatePreview();
    console.log('ChatService.initializeChat: Initial preview updated');

    // Clear messages subject before starting fresh or restoring
    this.messagesSubject.next([]);
    
    // If we have saved messages, restore them first
    if (savedMessages.length > 0) {
      console.log('🔄 ChatService: Restoring', savedMessages.length, 'messages from IndexedDB');
      this.messagesSubject.next(savedMessages);
      console.log('✅ ChatService: Messages restored successfully');
    } else {
      // For new sessions, AI control is enabled immediately
      // The AI conversation service will handle the initial greeting and data collection
      console.log('🤖 New session - AI control enabled, letting AI handle conversation');
    }
    
    console.log('ChatService.initializeChat: Complete');
  }

  private async getOrSelectTemplate(): Promise<number> {
    try {
      // Check if we already have a selected template stored
      const savedTemplate = await this.indexedDBService.getMetadata('selectedTemplate');
      
      if (savedTemplate && savedTemplate >= 1 && savedTemplate <= 5) {
        console.log('ChatService.getOrSelectTemplate: Using saved template:', savedTemplate);
        return savedTemplate;
      } else {
        // First time visit - select random template and save it
        // const randomTemplate = Math.floor(Math.random() * 5) + 1;
        const randomTemplate = 1;
        await this.indexedDBService.storeMetadata('selectedTemplate', randomTemplate);
        console.log('ChatService.getOrSelectTemplate: First visit - selected and saved template:', randomTemplate);
        return randomTemplate;
      }
    } catch (error) {
      console.warn('Failed to access template from IndexedDB, using random:', error);
      // Fallback to random template if IndexedDB fails
      return Math.floor(Math.random() * 5) + 1;
    }
  }

  // startDataCollection() method removed - AI control handles all conversation flow now

  private async askNextQuestion(): Promise<void> {
    const currentPhase = this.currentPhaseSubject.value;
    
    // If AI control is enabled, don't continue with traditional questions
    if (this.isAIControlEnabled()) {
      console.log('🤖 ChatService: AI control is enabled, skipping traditional question flow');
      return;
    }
    
    // For AI-driven flow, we don't use traditional question progression
    console.log('🔄 ChatService: Traditional question flow not used in AI-driven mode');
  }

  private async showSingleQuestion(question: Question): Promise<void> {
    if (!question) return;

    // Reset phase to DATA_COLLECTION for regular questions
    this.currentPhaseSubject.next(ChatPhase.DATA_COLLECTION);
    
    // Check if this question has already been asked (prevent duplicates)
    const currentMessages = this.messagesSubject.value;
    const questionText = question.question || '';
    const hasBeenAsked = currentMessages.some(msg => 
      msg.content.includes(questionText) && msg.isBot
    );
    
    if (hasBeenAsked) {
      console.log('⚠️ Question already asked, skipping:', questionText);
      return;
    }
    
    const helpText = question.help ? `<div class="help-text">${question.help}</div>` : '';
    const optionalText = question.optional ? ' <span class="optional-tag">(Optional)</span>' : '';
    
    // Add the question message to chat
    await this.addMessage(`
      <div class="question-container">
        <div class="question-text">${question.question}${optionalText}</div>
        ${helpText}
      </div>
    `);

    // Handle form questions
    if (question.type === 'form') {
      await this.showFormQuestion(question);
    }
  }

  // Color selection is now handled in preview header - method removed

  private async showFormQuestion(question: Question): Promise<void> {
    if (question.fields && question.fields.length > 0) {
      const formFields = question.fields
        .map(field => `- ${field.label}${field.optional ? ' (Optional)' : ''}`)
        .join('\n');
      await this.addMessage(`Please fill in the following information:\n${formFields}`);
    }
  }

  async handleUserInput(input: string): Promise<boolean> {
    console.log('🔄 ChatService.handleUserInput: Starting...', { input });
    
    // In AI-driven flow, traditional question handling is not used
    // This method is kept for backward compatibility but should not be called
    console.log('⚠️ ChatService.handleUserInput: Traditional input handling called in AI-driven flow');
      return false;
  }

  async handleFileUpload(file: File, dataUrl: string): Promise<boolean> {
    // In AI-driven flow, file uploads are handled differently
    console.log('⚠️ ChatService.handleFileUpload: Traditional file handling called in AI-driven flow');
      return false;
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async handleFormSubmission(formData: any): Promise<boolean> {
    // In AI-driven flow, form submissions are handled differently
    console.log('⚠️ ChatService.handleFormSubmission: Traditional form handling called in AI-driven flow');
      return false;
    }

  private async displayFormResponses(fields: FormField[], formData: any): Promise<void> {
    // Stub method for compatibility
    console.log('ChatService.displayFormResponses: Stub method called');
  }

  private async storeSupplierDataToIndexedDB(supplierData: any): Promise<void> {
    try {
      await this.indexedDBService.setUserSupplierData(this.companyGuid, supplierData);
      console.log('📦 Supplier data stored to IndexedDB for company:', this.companyGuid);
    } catch (error) {
      console.warn('Failed to store supplier data to IndexedDB:', error);
    }
  }

  private async finishCollection(): Promise<void> {
    // All questions completed
    await this.addMessage(`🎉 Excellent! I've collected all your information and your website is ready.`);
    // Note: AI generation is now triggered by individual questions with triggerAIGeneration flag
    // Color selection is now handled as a regular question in the flow
  }

  /**
   * Start AI content generation phase
   */
  private async startAIContentGeneration(): Promise<void> {
    // await this.addMessage(`
    //   <div style="background: linear-gradient(135deg, #e3f2fd, #bbdefb); border-radius: 15px; padding: 1.5rem; text-align: center; margin: 1rem 0;">
    //     <h3>🤖 Enhancing Your Content with AI</h3>
    //     <p>I'm now generating professional, manufacturing-focused content for your website based on your information.</p>
    //     <p>This will only take a moment...</p>
    //   </div>
    // `);

    this.currentPhaseSubject.next(ChatPhase.AI_GENERATION);
    this.isGeneratingAIContentSubject.next(true);

    try {
      await this.generateAndApplyAIContent();
    } catch (error) {
      console.error('AI content generation failed:', error);
      await this.handleAIGenerationError(error);
    }
  }

  /**
   * Generate AI content and automatically apply it to the website
   */
  private async generateAndApplyAIContent(): Promise<void> {
    const supplierData = this.supplierDataSubject.value;

    try {
      // Get template HTML content (for API context, but we'll apply to all files)
      const templateHtml = await this.previewService.getTemplateHtml();
      
      // Generate AI content for all semantic tags
      const businessData = this.aiContentService.convertSupplierDataToBusinessData(supplierData);
      const response = await this.aiContentService.generateTagBasedContent({
        business_data: [businessData],
        template_html: templateHtml,
        content_preferences: this.aiContentService.getDefaultContentPreferences()
      });

      if (response.status === 'success' && response.data) {
        // Check if response.data is TagReplacement[] or DictContentStructure
        let allGeneratedTags: TagReplacement[];
        
        if (Array.isArray(response.data)) {
          // Handle TagReplacement[] format
          allGeneratedTags = response.data.map((item: TagReplacement) => ({
            tagName: item.tagName,
            replacementHTML: item.replacementHTML
          }));
        } else {
          // Handle DictContentStructure format - convert to TagReplacement[]
          allGeneratedTags = this.convertDictToTagReplacements(response.data as DictContentStructure);
        }

        // Apply all generated content to preview (this will apply to all HTML files)
        await this.previewService.applyAIContent(allGeneratedTags, this.companyGuid);
        
        // Create content updates for tracking (mark all as changed since they're AI-generated)
        const contentUpdates: ContentUpdate[] = allGeneratedTags.map(tag => ({
          tagName: tag.tagName,
          context: 'ai-generated',
          originalContent: '',
          newContent: tag.replacementHTML,
          isChanged: true
        }));
        
        // Store content updates for tracking
        this.contentUpdatesSubject.next(contentUpdates);
        
        // Update AI generation status
        this.aiContentGenerationSubject.next({
          status: 'completed',
          tagReplacements: allGeneratedTags,
          hasChanges: true
        });

        // Save complete state after AI content is applied
        await this.saveStateOnAIContentApplied();

        // Content generation completed successfully
        await this.addMessage(`✅ Your website content is updated and ready for preview!`);
      } else {
        await this.addMessage(`✅ Your content is already updated and ready for preview!`);
        this.aiContentGenerationSubject.next({
          status: 'completed',
          tagReplacements: [],
          hasChanges: false
        });
      }

      // NOTE: DO NOT transition to deployment phase here
      // The proper flow is handled by AI conversation service:
      // AI termination → logo upload → content generation → deployment
      // This method is called by the component after logo upload is completed
      
      console.log('✅ AI content generation complete - waiting for component to handle next phase transition');

    } catch (error) {
      console.error('AI content generation error:', error);
      throw error;
    } finally {
      // Ensure AI generation status is always reset
      this.isGeneratingAIContentSubject.next(false);
    }
  }

  /**
   * Handle AI generation error
   */
  private async handleAIGenerationError(error: any): Promise<void> {
    this.aiContentGenerationSubject.next({
      status: 'error',
      tagReplacements: [],
      hasChanges: false,
      errorMessage: error.message || 'Failed to generate AI content'
    });

    await this.addMessage(`⚠️ I encountered an issue generating AI content. Don't worry - your website is still ready!`);
    await this.sleep(500);
    await this.addMessage(`I'll proceed with the existing content. You can always update it later.`);
    
    // NOTE: DO NOT transition to deployment phase here either
    // Let the AI conversation service handle the proper flow even after errors
    console.log('🔄 AI content generation error handled - waiting for component to handle next phase transition');
    
    this.isGeneratingAIContentSubject.next(false);
  }

  /**
   * Color selection is now handled in preview header - method deprecated
   */
  private async proceedToColorSelection(): Promise<void> {
    // Color selection is now handled via theme picker in preview header
    // Skip directly to next phase
    this.currentPhaseSubject.next(ChatPhase.DATA_COLLECTION);
  }

  async selectColorTheme(theme: string): Promise<void> {
    // Direct theme selection for AI-driven flow
      this.selectedColorThemeSubject.next(theme);
      await this.updatePreview();
  }

  /**
   * Preview a color theme without completing the question or proceeding to next step
   */
  async previewColorTheme(theme: string): Promise<void> {
    console.log('🔄 ChatService.previewColorTheme: Updating preview with theme:', theme);
    
    // Update the selected theme for preview
    this.selectedColorThemeSubject.next(theme);
    
    // Update preview with new theme
    try {
      await this.updatePreview();
      console.log('✅ ChatService.previewColorTheme: Preview updated successfully');
    } catch (error) {
      console.error('❌ ChatService.previewColorTheme: Preview update error:', error);
    }
  }

  async handleColorSelection(theme: string): Promise<boolean> {
    console.log('🔄 ChatService.handleColorSelection: Starting...', { theme });
    
    // Store the selected theme
    this.selectedColorThemeSubject.next(theme || 'default');
    await this.updatePreview();
    
      // Add user message showing selection
      const selectedTheme = this.colorThemes[theme] || this.colorThemes['default'];
      await this.addMessage(`🎨 ${selectedTheme.name} theme selected`, false);
    
    // Transition from color selection to data collection phase
    this.currentPhaseSubject.next(ChatPhase.DATA_COLLECTION);
    
    // Enable AI control after theme selection
    console.log('🤖 Theme selection complete - enabling AI control for data collection');
      this.enableAIControl();
      
    console.log('✅ ChatService.handleColorSelection: Complete!');
    return true;
  }

  async deployWebsite(): Promise<void> {
    try {
      // Single consolidated message for Phase 1
      await this.addMessage('🚀 Perfect! Your website looks great. Let\'s make it live for your customers. This typically takes 3-5 minutes...');
      
      const websiteZip = await this.previewService.getDeploymentZip();
      const fileCount = this.previewService.getFileCount();
      
      // Generate folder name from company name
      const supplierData = this.supplierDataSubject.value;
      const folderName = this.sanitizeFolderName(supplierData.companyName);

      // Start deployment state and transition to deployment phase
      this.currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
      this.isDeployingSubject.next(true);
      
      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('website_zip', websiteZip, 'website.zip');
        formData.append('folder_name', folderName);
        
        // Deploy using new file upload endpoint
        const deployResult: any = await firstValueFrom(
          this.websiteGenerationService.deployWebsiteFiles(formData)
        );
        
        if (deployResult.success) {
          // Start polling deployment status (no additional success message)
          this.pollDeploymentStatus(deployResult.deploymentId);
        } else {
          await this.addMessage(`${ChatErrorMessages.UNABLE_TO_PUBLISH} ${deployResult.message}`);
          this.isDeployingSubject.next(false); // End deployment state on failure
        }
        
      } catch (error) {
        console.error('Website publishing failed:', error);
        this.isDeployingSubject.next(false); // End deployment state on error
        this.currentPhaseSubject.next(ChatPhase.COMPLETED); // Transition to completed phase
        await this.addMessage(ChatErrorMessages.WEBSITE_PUBLISHING_FAILED);
      }
    } catch (error) {
      console.error('Website packaging failed:', error);
      this.isDeployingSubject.next(false); // End deployment state on error
      this.currentPhaseSubject.next(ChatPhase.COMPLETED); // Transition to completed phase
      await this.addMessage(`❌ We couldn\'t package your website files. Let\'s try again: ${error}`);
    }
  }

  private sanitizeFolderName(name: string): string {
    if (!name) return 'unnamed-company';
    return name.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '') || 'unnamed-company';
  }

  private async pollDeploymentStatus(deploymentId: string): Promise<void> {
    let attempts = 0;
    const config = environment.deploymentConfig;
    const maxAttempts = config.maxAttempts;
    const pollInterval = config.pollInterval;
    
    let potentialWebsiteUrl = '';
    
    const checkStatus = async () => {
      if (attempts >= maxAttempts) {
        this.isDeployingSubject.next(false); // End deployment state on timeout
        this.currentPhaseSubject.next(ChatPhase.COMPLETED); // Transition to completed phase
        // Even after timeout, show URL if we have one
        if (potentialWebsiteUrl) {
          await this.addMessage(ChatTimeoutMessages.DEPLOYMENT_LONGER_EXPECTED);
          await this.addMessage(`${ChatTimeoutMessages.WEBSITE_MIGHT_BE_READY} <a href="${potentialWebsiteUrl}" target="_blank">${potentialWebsiteUrl}</a>`);
          await this.addMessage(ChatTimeoutMessages.BOOKMARK_ARGOCD_URL);
        } else {
          await this.addMessage(ChatTimeoutMessages.CHECK_BACK_MINUTES);
        }
        return;
      }
      
      try {
        const status = await firstValueFrom(this.websiteGenerationService.getDeploymentStatus(deploymentId));
        
        // Store potential website URL for later use
        if (status.websiteUrl) {
          potentialWebsiteUrl = status.websiteUrl;
        }
        
        // Check if deployment is complete
        if (status.status === DeploymentStatusTypes.DONE) {
          if (status.websiteUrl) {
            // Start website accessibility checking phase
            await this.checkWebsiteAccessibility(status.websiteUrl);
          } else {
            // No URL provided, end deployment
            this.isDeployingSubject.next(false);
            this.currentPhaseSubject.next(ChatPhase.COMPLETED);
            await this.addMessage(DeploymentSuccessMessages.WEBSITE_READY);
          }
          return;
        } else if (status.status === DeploymentStatusTypes.FAILED) {
          this.isDeployingSubject.next(false); // End deployment state
          this.currentPhaseSubject.next(ChatPhase.COMPLETED); // Transition to completed phase even on failure
          // Show URL even on failure
          if (status.websiteUrl || potentialWebsiteUrl) {
            const urlToShow = status.websiteUrl || potentialWebsiteUrl;
            await this.addMessage(ChatFailureRecoveryMessages.DEPLOYMENT_ISSUE);
            await this.addMessage(`${ChatFailureRecoveryMessages.WEBSITE_MIGHT_BE_AVAILABLE} <a href="${urlToShow}" target="_blank">${urlToShow}</a>`);
            await this.addMessage(ChatFailureRecoveryMessages.TRY_IN_FEW_MINUTES);
            await this.addMessage(ChatFailureRecoveryMessages.CONTACT_SUPPORT);
          } else {
            await this.addMessage(ChatErrorMessages.SOMETHING_WENT_WRONG);
          }
          return;
        }
        
        // Continue polling if still in progress - no status messages shown during polling
        attempts++;
        setTimeout(checkStatus, pollInterval);
        
      } catch (error) {
        console.error('Error checking deployment status:', error);
        this.isDeployingSubject.next(false); // End deployment state on error
        this.currentPhaseSubject.next(ChatPhase.COMPLETED); // Transition to completed phase
        
        // Show URL even on error if we have one
        if (potentialWebsiteUrl) {
          await this.addMessage(ChatStatusCheckMessages.UNABLE_TO_CHECK_STATUS);
          await this.addMessage(`${ChatStatusCheckMessages.TRY_VISITING_URL} <a href="${potentialWebsiteUrl}" target="_blank">${potentialWebsiteUrl}</a>`);
          await this.addMessage(ChatStatusCheckMessages.WAIT_FOR_ARGOCD);
        } else {
          await this.addMessage(ChatStatusCheckMessages.STATUS_CHECK_FAILED);
        }
      }
    };
    
    // Start checking status with initial delay
    setTimeout(checkStatus, pollInterval);
  }

  private async updatePreview(): Promise<void> {
    console.log('🔄 ChatService.updatePreview: Starting...');
    
    try {
      const supplierData = this.supplierDataSubject.value;
      const template = this.selectedTemplateSubject.value;
      const theme = this.selectedColorThemeSubject.value;
      
      console.log('🔄 Calling PreviewService.updatePreview with:', {
        companyName: supplierData.companyName,
        template,
        theme
      });
      
      await this.previewService.updatePreview(
        supplierData,
        template,
        theme,
        this.companyGuid
      );
      
      // CRITICAL FIX: Reapply AI-generated content after preview update
      await this.reapplyStoredAIContent();
      
      console.log('✅ ChatService.updatePreview: Complete!');
    } catch (error) {
      console.error('❌ ChatService.updatePreview: Failed with error:', error);
      throw error; // Re-throw so calling methods can handle it
    }
  }

  /**
   * Reapply stored AI-generated content after preview updates
   */
  private async reapplyStoredAIContent(): Promise<void> {
    try {
      const contentUpdates = this.contentUpdatesSubject.value;
      const aiGeneration = this.aiContentGenerationSubject.value;
      
      // Check if we have AI-generated content to reapply
      if (contentUpdates.length > 0 && aiGeneration.hasChanges) {
        console.log('🤖 Reapplying stored AI content after preview update...');
        
        // Get tag replacements from stored content updates
        const tagReplacements = contentUpdates
          .filter(update => update.isChanged)
          .map(update => ({
            tagName: update.tagName,
            replacementHTML: update.newContent
          }));
        
        if (tagReplacements.length > 0) {
          await this.previewService.applyAIContent(tagReplacements, this.companyGuid);
          console.log('✅ AI content reapplied successfully');
        }
      }
    } catch (error) {
      console.error('❌ Failed to reapply AI content:', error);
      // Don't throw - this is a non-critical enhancement
    }
  }

  async addMessage(content: string, isBot: boolean = true): Promise<void> {
    const message: ChatMessage = {
      id: Date.now().toString(),
      content,
      isBot,
      timestamp: new Date()
    };

    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  toggleMinimize(): void {
    this.isMinimizedSubject.next(!this.isMinimizedSubject.value);
  }

  getSupplierData(): SupplierData {
    return this.supplierDataSubject.value;
  }

  /**
   * Update supplier data and invalidate cache
   */
  updateSupplierData(data: Partial<SupplierData>): void {
    const currentData = this.supplierDataSubject.value;
    const updatedData = { ...currentData, ...data };
    this.supplierDataSubject.next(updatedData);
    this.invalidateDataCollectionCache();
  }

  getCompanyGuid(): string {
    return this.companyGuid;
  }

  setCompanyGuid(guid: string): void {
    console.log('ChatService: Setting company GUID to:', guid);
    this.companyGuid = guid;
  }

  isDeploymentInProgress(): boolean {
    return this.isDeployingSubject.value;
  }

  async loadTemplatePage(pageName: string): Promise<void> {
    console.log('ChatService.loadTemplatePage: Loading page:', pageName);
    
    // Get current supplier data to pass to preview service
    const currentSupplierData = this.getSupplierData();
    
    // Call preview service to actually load the page
    await this.previewService.loadTemplatePage(pageName, currentSupplierData, this.companyGuid);
  }

  // AI Control Methods
  enableAIControl(): void {
    console.log('🤖 ChatService: AI control enabled - switching from traditional to AI flow');
    this.aiControlEnabledSubject.next(true);
  }

  /**
   * Check if AI control is enabled
   */
  isAIControlEnabled(): boolean {
    return this.aiControlEnabledSubject.value;
  }

  /**
   * Disable AI control (for fallback scenarios)
   */
  disableAIControl(): void {
    console.log('🔄 ChatService: AI control disabled - falling back to traditional flow');
    this.aiControlEnabledSubject.next(false);
  }

  /**
   * Chat termination function that checks if all required fields are collected
   * This is the main function that determines when to show the Deploy Now button
   */
  isDataCollectionComplete(): boolean {
    // Check cache first to prevent infinite loops
    const now = Date.now();
    if (this.dataCollectionCompleteCache && 
        (now - this.dataCollectionCompleteCache.timestamp) < this.CACHE_DURATION) {
      return this.dataCollectionCompleteCache.result;
    }

    // CRITICAL FIX: Don't consider data collection complete if AI is actively controlling the conversation
    // This prevents premature deployment phase transitions during ongoing AI conversations
    if (this.isAIControlEnabled()) {
      const result = false;
      this.dataCollectionCompleteCache = { result, timestamp: now };
      return result;
    }
    
    // CRITICAL FIX: Don't consider data collection complete if we're still in early phases
    const currentPhase = this.currentPhaseSubject.value;
    if (currentPhase === ChatPhase.AI_GENERATION) {
      const result = false;
      this.dataCollectionCompleteCache = { result, timestamp: now };
      return result;
    }
    
    // CRITICAL FIX: Ensure questions are initialized before checking completion
    if (this.questions.length === 0) {
      const result = false;
      this.dataCollectionCompleteCache = { result, timestamp: now };
      return result;
    }
    
    const supplierData = this.getSupplierData();
    const requiredFields = this.getRequiredFields();
    
    // CRITICAL FIX: Ensure we have a substantial amount of data, not just minimal fields
    // Check for essential business data that indicates a proper conversation completion
    const essentialFields = ['companyName', 'contactEmail', 'aboutContent'];
    for (const field of essentialFields) {
      const value = supplierData[field as keyof SupplierData];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const result = false;
        this.dataCollectionCompleteCache = { result, timestamp: now };
        return result;
      }
    }
    
    // Check if all required fields are filled
    for (const field of requiredFields) {
      const value = supplierData[field as keyof SupplierData];
      
      // Handle different field types
      if (field === 'capabilities') {
        // Capabilities should be a non-empty array or string
        if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '')) {
          const result = false;
          this.dataCollectionCompleteCache = { result, timestamp: now };
          return result;
        }
      } else if (typeof value === 'string') {
        // String fields should not be empty
        if (value.trim() === '') {
          const result = false;
          this.dataCollectionCompleteCache = { result, timestamp: now };
          return result;
        }
      } else if (value === null || value === undefined) {
        const result = false;
        this.dataCollectionCompleteCache = { result, timestamp: now };
        return result;
      }
    }
    
    const result = true;
    this.dataCollectionCompleteCache = { result, timestamp: now };
    return result;
  }

  /**
   * Get the list of required fields from the questions array
   */
  private getRequiredFields(): string[] {
    const requiredFields: string[] = [];
    
    // Safety check: if questions are not initialized, return empty array
    if (this.questions.length === 0) {
      return requiredFields;
    }
    
    for (const question of this.questions) {
      if (question.optional) {
        continue; // Skip optional fields
      }
      
      if (question.type === 'form' && question.fields) {
        // Handle form questions with multiple fields
        for (const field of question.fields) {
          if (!field.optional) {
            requiredFields.push(field.field);
          }
        }
      } else if (question.field) {
        // Handle single field questions
        requiredFields.push(question.field);
      }
    }
    
    return requiredFields;
  }

  /**
   * Get missing required fields
   */
  getMissingRequiredFields(): string[] {
    const supplierData = this.getSupplierData();
    const requiredFields = this.getRequiredFields();
    const missingFields: string[] = [];
    
    for (const field of requiredFields) {
      const value = supplierData[field as keyof SupplierData];
      
      if (field === 'capabilities') {
        if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '')) {
          missingFields.push(field);
        }
      } else if (typeof value === 'string') {
        if (value.trim() === '') {
          missingFields.push(field);
        }
      } else if (value === null || value === undefined) {
        missingFields.push(field);
      }
    }
    
    return missingFields;
  }

  /**
   * Invalidate the data collection completion cache
   * Call this when supplier data or other relevant state changes
   */
  private invalidateDataCollectionCache(): void {
    this.dataCollectionCompleteCache = null;
  }

  /**
   * Check if chat should be terminated (all required data collected)
   */
  shouldTerminateChat(): boolean {
    const isComplete = this.isDataCollectionComplete();
    const phase = this.currentPhaseSubject.value;
    
    // Only terminate if data collection is complete and we're in the right phase
    return isComplete && (phase === ChatPhase.DATA_COLLECTION);
  }

  /**
   * Force chat termination and show deploy button
   */
  terminateChat(): void {
    console.log('🏁 ChatService: Terminating chat - all required data collected');
    
    // Update phase to show deployment is ready
    this.currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
    
    // Add completion message
    this.addMessage(`
      <div style="background: linear-gradient(135deg, #28a745, #20c997); color: white; border-radius: 15px; padding: 1.5rem; text-align: center; margin: 1rem 0;">
        <div style="font-size: 1.2rem; font-weight: bold; margin-bottom: 0.5rem;">
          🎉 All Set! Your Website is Ready
        </div>
        <div style="font-size: 0.9rem; opacity: 0.9;">
          I've collected all the necessary information. You can now deploy your website or make any changes if needed.
        </div>
      </div>
    `);
  }

  /**
   * Trigger AI content generation (public interface for external components)
   */
  async triggerAIContentGeneration(): Promise<void> {
    this.currentPhaseSubject.next(ChatPhase.AI_GENERATION);
    this.isGeneratingAIContentSubject.next(true);
    
    // Trigger AI content generation
    await this.generateAndApplyAIContent();
  }

  /**
   * Reset the chat service to its initial state
   */
  resetToInitialState(): void {
    // Reset all BehaviorSubjects to their initial values
    // this.currentStepSubject.next(0); // Removed as per edit hint
    this.messagesSubject.next([]);
    // this.isNavigatingSubject.next(false); // Removed as per edit hint
    // this.completedStepsSubject.next(new Set()); // Removed as per edit hint
    
    // Reset supplier data to initial empty state
    this.supplierDataSubject.next({
      companyName: '',
      phoneNumber: '',
      city: '',
      countryRegion: '',
      websiteURL: '',
      streetAddress: '',
      streetAddress2: '',
      logoURL: '',
      contactEmail: '',
      postalCode: '',
      equipment: '',
      capabilities: '',
      aboutContent: ''
    });
    this.invalidateDataCollectionCache();
    
    this.selectedTemplateSubject.next(1);
    this.selectedColorThemeSubject.next('default');
    this.isMinimizedSubject.next(false);
    this.isDeployingSubject.next(false);
    this.currentPhaseSubject.next(ChatPhase.DATA_COLLECTION);
    
    // Reset AI content generation state
    this.aiContentGenerationSubject.next({
      status: 'generating',
      tagReplacements: [],
      hasChanges: false
    });
    
    this.isGeneratingAIContentSubject.next(false);
    this.contentUpdatesSubject.next([]);
    this.aiControlEnabledSubject.next(false);
    
    // Reset questions array
    this.questions = [];
    
    // Generate new company GUID
    this.companyGuid = this.websiteGenerationService.generateGUID();
    
    console.log('✅ ChatService reset to initial state');
  }

  /**
   * Force initialize chat without checking for restored session
   */
  async forceInitializeChat(): Promise<void> {
    console.log('ChatService.forceInitializeChat: Starting fresh...');
    
    // Initialize questions for new session (no restoration)
    this.initializeQuestions(false);

    // Get or select template for new sessions
    let selectedTemplate = Math.floor(Math.random() * 5) + 1; // Force new random template
    console.log('ChatService.forceInitializeChat: Selected new template:', selectedTemplate);
    this.selectedTemplateSubject.next(selectedTemplate);

    // Store the new template
    await this.indexedDBService.storeMetadata('selectedTemplate', selectedTemplate);

    // Initialize preview
    console.log('ChatService.forceInitializeChat: Initializing preview...');
    await this.previewService.initializePreview(selectedTemplate);
    console.log('ChatService.forceInitializeChat: Preview initialized');


    // Update preview with initial data
    console.log('ChatService.forceInitializeChat: Updating initial preview...');
    await this.updatePreview();
    console.log('ChatService.forceInitializeChat: Initial preview updated');

    // Clear messages subject - AI control will handle conversation flow
    this.messagesSubject.next([]);
    
    console.log('ChatService.forceInitializeChat: Complete');
  }

  /**
   * Public method to reapply stored AI content (for use by external services)
   */
  async reapplyAIContent(): Promise<void> {
    await this.reapplyStoredAIContent();
  }

  /**
   * Store complete application state to IndexedDB
   */
  async storeCompleteState(): Promise<void> {
    try {
      const currentState = {
        messages: this.messagesSubject.value,
        supplierData: this.supplierDataSubject.value,
        selectedTemplate: this.selectedTemplateSubject.value,
        selectedColorTheme: this.selectedColorThemeSubject.value,
        isMinimized: this.isMinimizedSubject.value,
        isDeploying: this.isDeployingSubject.value,
        currentPhase: this.currentPhaseSubject.value,
        aiContentGeneration: this.aiContentGenerationSubject.value,
        isGeneratingAIContent: this.isGeneratingAIContentSubject.value,
        contentUpdates: this.contentUpdatesSubject.value,
        aiControlEnabled: this.aiControlEnabledSubject.value,
        questions: this.questions,
        editMode: this.previewService.getEditMode(),
        lastSaved: new Date().toISOString()
      };

      await this.indexedDBService.storeAllUserData(this.companyGuid, currentState);
      console.log('✅ Complete application state stored to IndexedDB');
    } catch (error) {
      console.error('❌ Failed to store complete state:', error);
    }
  }

  /**
   * Restore complete application state from IndexedDB
   */
  private async restoreCompleteState(): Promise<void> {
    try {
      const savedState = await this.indexedDBService.getAllUserData(this.companyGuid);
      
      if (savedState) {
        console.log('🔄 Restoring complete application state from IndexedDB...');
        
        // Restore all BehaviorSubjects (except messages - handled separately in initializeChat)
        // if (savedState.messages) {
        //   this.messagesSubject.next(savedState.messages);
        // }
        
        if (savedState.supplierData) {
          this.supplierDataSubject.next(savedState.supplierData);
          this.invalidateDataCollectionCache();
        }
        
        if (savedState.selectedTemplate) {
          this.selectedTemplateSubject.next(savedState.selectedTemplate);
        }
        
        if (savedState.selectedColorTheme) {
          this.selectedColorThemeSubject.next(savedState.selectedColorTheme);
        }
        
        if (savedState.isMinimized !== undefined) {
          this.isMinimizedSubject.next(savedState.isMinimized);
        }
        
        if (savedState.isDeploying !== undefined) {
          this.isDeployingSubject.next(savedState.isDeploying);
        }
        
        if (savedState.currentPhase) {
          this.currentPhaseSubject.next(savedState.currentPhase);
        }
        
        if (savedState.aiContentGeneration) {
          this.aiContentGenerationSubject.next(savedState.aiContentGeneration);
        }
        
        if (savedState.isGeneratingAIContent !== undefined) {
          this.isGeneratingAIContentSubject.next(savedState.isGeneratingAIContent);
        }
        
        if (savedState.contentUpdates) {
          this.contentUpdatesSubject.next(savedState.contentUpdates);
        }
        
        if (savedState.aiControlEnabled !== undefined) {
          this.aiControlEnabledSubject.next(savedState.aiControlEnabled);
        }
        
        if (savedState.questions) {
          this.questions = savedState.questions;
        }
        
        // Restore edit mode state
        if (savedState.editMode !== undefined) {
          await this.previewService.setEditMode(savedState.editMode, this.companyGuid);
        }
        
        console.log('✅ Complete application state restored from IndexedDB');
      } else {
        console.log('ℹ️ No saved state found in IndexedDB');
      }
    } catch (error) {
      console.error('❌ Failed to restore complete state:', error);
    }
  }

  /**
   * Auto-save state periodically
   */
  startAutoSave(): void {
    // Save state every 30 seconds
    setInterval(async () => {
      await this.storeCompleteState();
    }, 30000);
  }

  /**
   * Save state when AI content is applied
   */
  async saveStateOnAIContentApplied(): Promise<void> {
    await this.storeCompleteState();
  }

  /**
   * Check website accessibility - simplified stub
   */
  private async checkWebsiteAccessibility(websiteUrl: string): Promise<void> {
    // Simplified accessibility check - just show success message
    this.isDeployingSubject.next(false);
    this.currentPhaseSubject.next(ChatPhase.COMPLETED);
    await this.addMessage(`🎉 Your website is now live! <a href="${websiteUrl}" target="_blank">${websiteUrl}</a>`);
  }

  /**
   * Convert DictContentStructure to TagReplacement[] format
   */
  private convertDictToTagReplacements(dictContent: DictContentStructure): TagReplacement[] {
    const flatContent = this.flattenDictContent(dictContent);
    return Object.entries(flatContent).map(([key, value]) => ({
      tagName: key,
      replacementHTML: value
    }));
  }

  /**
   * Flatten nested dict structure into key-value pairs
   */
  private flattenDictContent(obj: any, prefix: string = ''): Record<string, string> {
    const result: Record<string, string> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recursively flatten nested objects
        Object.assign(result, this.flattenDictContent(value, newKey));
      } else if (typeof value === 'string') {
        // Store string values
        result[newKey] = value;
      }
    }
    
    return result;
  }


} 